import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Box,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  TextField,
  InputLabel,
  FormControl,
  Tooltip,
  Typography,
  Alert,
  LinearProgress,
  Chip
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import Loading from "./Loading";
import {
  FILE_TYPE,
  REQUIRED_DOCS,
  DOC_HELP,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_BYTES
} from "../constants";
import { createDoc, deleteDoc, downloadDoc } from "../services/docsServices";
import { useSnackbar } from "./SnackbarProvider";
import DocAutorizacion from "../assets/CartaAutorizacion.pdf";
import DocReconocimiento from "../assets/Reconocimiento.docx";

const TEMPLATE_MAP = {
  CartaAutorizacion: { file: DocAutorizacion, name: "Carta de Autorización.pdf" },
  Reconocimiento: { file: DocReconocimiento, name: "Reconocimiento de Deuda.docx" }
};

const DocumentsForm = ({ docs, consultantId, onModelChange }) => {
  const { showSnackbar } = useSnackbar();
  const [files, setFiles] = useState([]);
  const [fileType, setFileType] = useState(FILE_TYPE.DPI);
  const [showLoading, setShowLoading] = useState(false);
  const [loadText, setLoadText] = useState("Guardando");
  const [upFile, setUpFile] = useState(null);
  const [note, setNote] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setFiles(docs);
  }, [docs]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles && rejectedFiles.length > 0) {
        const error = rejectedFiles[0].errors[0];
        if (error.code === "file-too-large") {
          showSnackbar(`El archivo excede el tamaño máximo de ${MAX_FILE_SIZE_MB}MB`, "error");
        } else {
          showSnackbar("Formato de archivo no válido", "error");
        }
        return;
      }
      if (acceptedFiles.length > 0) {
        setUpFile(acceptedFiles[0]);
      }
    },
    [showSnackbar]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"]
    },
    multiple: false,
    maxSize: MAX_FILE_SIZE_BYTES
  });

  const handleUpload = async () => {
    if (!upFile) return;
    setIsUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append("document", upFile);
    formData.append("type", fileType);
    formData.append("consultantId", consultantId);
    formData.append("note", note);
    try {
      const response = await createDoc(formData, (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      });
      setUpFile(null);
      setNote("");
      onModelChange([...files, response]);
      showSnackbar("Documento subido correctamente");
    } catch (error) {
      showSnackbar("Error al subir el archivo", "error");
    }
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleDelete = async (fileId) => {
    setLoadText("Eliminando archivo");
    setShowLoading(true);
    try {
      await deleteDoc(fileId);
      onModelChange(files.filter((file) => file.id !== fileId));
      showSnackbar("Documento eliminado");
    } catch (error) {
      showSnackbar("Error al eliminar el archivo", "error");
    }
    setShowLoading(false);
  };

  const handleDownload = async (fileId) => {
    setLoadText("Descargando archivo");
    setShowLoading(true);
    try {
      const response = await downloadDoc(fileId);
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: response.headers["content-type"] })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileId);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showSnackbar("Error al descargar el archivo", "error");
    }
    setShowLoading(false);
  };

  const isDocUploaded = (docType) => files.some((f) => f.type === docType);
  const getUploadedDoc = (docType) => files.find((f) => f.type === docType);

  const currentHelp = DOC_HELP[fileType];
  const template = currentHelp?.templateKey ? TEMPLATE_MAP[currentHelp.templateKey] : null;

  return (
    <Box sx={{ maxWidth: "85%", margin: "auto", padding: 2 }}>
      {/* Required Documents Checklist */}
      <Typography variant="h6" gutterBottom>
        Documentos obligatorios
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {REQUIRED_DOCS.map((docType) => {
          const uploaded = isDocUploaded(docType);
          const doc = getUploadedDoc(docType);
          return (
            <Grid item xs={12} sm={6} key={docType}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  borderColor: uploaded ? "success.main" : "warning.main"
                }}
              >
                {uploaded ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <RadioButtonUncheckedIcon color="warning" />
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {DOC_HELP[docType].shortLabel}
                  </Typography>
                  {uploaded && doc && (
                    <Typography variant="caption" color="text.secondary">
                      Subido el {new Date(doc.createdAt).toLocaleDateString("es-GT")}
                    </Typography>
                  )}
                  {!uploaded && (
                    <Typography variant="caption" color="warning.main">
                      Pendiente
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Upload Section */}
      <Typography variant="h6" gutterBottom>
        Subir documento
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Tipo de documento</InputLabel>
            <Select
              value={fileType}
              label="Tipo de documento"
              onChange={(e) => setFileType(e.target.value)}
            >
              {Object.entries(DOC_HELP).map(([key, info]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {info.label}
                    {REQUIRED_DOCS.includes(key) && (
                      <Chip label="Obligatorio" size="small" color="warning" />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nota (opcional)"
            type="text"
            variant="outlined"
            fullWidth
            inputProps={{ maxLength: 70 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Contextual Help */}
      {currentHelp && (
        <Alert severity="info" icon={<DescriptionIcon />} sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            {currentHelp.description}
          </Typography>
          {currentHelp.instructions && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {currentHelp.instructions}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            {template && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                href={template.file}
                download={template.name}
                target="_blank"
                rel="noreferrer"
              >
                Descargar plantilla
              </Button>
            )}
            {currentHelp.externalLink && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href={currentHelp.externalLink}
                target="_blank"
                rel="noreferrer"
              >
                Visitar sitio
              </Button>
            )}
          </Box>
        </Alert>
      )}

      {/* Drag and Drop Zone */}
      <Box
        {...getRootProps()}
        sx={{
          mt: 2,
          p: 3,
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : upFile ? "success.main" : "grey.400",
          borderRadius: 2,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragActive ? "action.hover" : "transparent",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover"
          }
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon
          sx={{ fontSize: 40, color: upFile ? "success.main" : "grey.500", mb: 1 }}
        />
        {isDragActive ? (
          <Typography color="primary">Suelta el archivo aquí</Typography>
        ) : upFile ? (
          <Box>
            <Typography fontWeight={500}>{upFile.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {(upFile.size / 1024 / 1024).toFixed(2)} MB — Haz clic o arrastra otro archivo para
              reemplazar
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography>Arrastra un archivo aquí o haz clic para seleccionar</Typography>
            <Typography variant="caption" color="text.secondary">
              Formatos: JPG, PNG, PDF, DOC, XLS (Máx. {MAX_FILE_SIZE_MB}MB)
            </Typography>
          </Box>
        )}
      </Box>

      {/* Upload Progress */}
      {isUploading && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Subiendo... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Upload Button */}
      <Button
        variant="contained"
        fullWidth
        startIcon={<FileUploadIcon />}
        disabled={!upFile || isUploading}
        onClick={handleUpload}
        sx={{ mt: 2 }}
      >
        Subir archivo
      </Button>

      {/* Uploaded Documents Table */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Documentos cargados
      </Typography>
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <TableCell>Tipo de archivo</TableCell>
              <TableCell>Fecha de carga</TableCell>
              <TableCell>Nota</TableCell>
              <TableCell sx={{ width: "96px" }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {files.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: "grey.400", mb: 1 }} />
                  <Typography color="text.secondary">
                    Aún no has subido documentos. Usa el formulario de arriba para cargar tu primer
                    documento.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              files.map((file, index) => (
                <TableRow key={index}>
                  <TableCell>{DOC_HELP[file.type]?.shortLabel || file.type}</TableCell>
                  <TableCell>{new Date(file.createdAt).toLocaleDateString("es-GT")}</TableCell>
                  <TableCell>{file.note || ""}</TableCell>
                  <TableCell>
                    <Tooltip title="Descarga tu documento">
                      <IconButton onClick={() => handleDownload(file.name)}>
                        <GetAppIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina el documento">
                      <IconButton onClick={() => handleDelete(file.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Loading show={showLoading} text={loadText} />
    </Box>
  );
};

export default DocumentsForm;

DocumentsForm.propTypes = {
  docs: PropTypes.array,
  consultantId: PropTypes.number,
  onModelChange: PropTypes.func.isRequired
};
